import React, { FC, useState, useRef } from 'react';
import { useCss } from 'app/utils/css';
import { COLOR_PALETTE } from 'app/constants/ColorPalette';
import { NavigationType, NavigationContext } from 'app/components/header/navigation/Navigation';
import { INavigationItemProps } from 'app/components/header/navigation/NavigationItem';
import { useOnFocusOutside } from 'app/utils/react';
import { HeaderStack } from 'app/components/header/HeaderView';
import Options from 'app/components/svgs/Options';
import Link from 'app/components/router/Link';
import { useHoverState } from '@engoo/react-hover-state/lib';
import { INavigationLinkProps } from 'app/components/header/navigation/NavigationLink';
import { INavigationSectionProps } from 'app/components/header/navigation/NavigationSection';

/**
 * @private Do not use directly except in tests and stories. Use {@link Navigation} instead
 */
export const DrawerNavigation: FC = ({ children }) => {
  const css = useCss();
  const [isOpen, setOpen] = useState(false);
  const close = () => setOpen(false);
  const toggle = () => setOpen(flag => !flag);
  const containerRef = useRef<HTMLUListElement | null>(null);
  useOnFocusOutside({
    getNode: () => containerRef.current,
    callback: close,
  });

  return (
    <NavigationContext.Provider value={{ type: NavigationType.Drawer }}>
      <NavigationToggle onClick={toggle} />
      <ul
        ref={containerRef}
        className={css('drawer', {
          backgroundColor: COLOR_PALETTE.grey.white,
          boxShadow: '0 0 4px rgba(0, 0, 0, 0.28)',
          fontSize: '12px',
          textAlign: 'left',
          height: '100%',
          width: '200px',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: HeaderStack.Navigation,
          transform: isOpen ? 'none' : 'translateX(-100%)',
          transition: 'transform 400ms',
          overflow: 'auto',
          listStyle: 'none',
          margin: 0,
          padding: 0,
        })}
      >
        {children}
      </ul>
    </NavigationContext.Provider>
  );
};

const NavigationToggle: FC<{ onClick?: () => void }> = ({ onClick }) => {
  const css = useCss();

  return (
    <div
      onClick={onClick}
      className={css('container', {
        alignItems: 'center',
        cursor: 'pointer',
        display: 'flex',
        alignSelf: 'stretch',
        padding: `1.5em 1em`,
      })}
    >
      <Options size="30px" />
    </div>
  );
};

/**
 * @private Do not use directly except in tests and stories. Use {@link NavigationSection} instead
 */
export const DrawerSection: FC<INavigationSectionProps> = ({
  children,
  ...props
}) => {
  const css = useCss();
  return (
    <li>
      <ul
        className={css('section', {
          listStyle: 'none',
          margin: 0,
          padding: 0,
          borderBottom: `1px solid ${COLOR_PALETTE.grey.base}`,
          ':empty': {
            display: 'none',
          },
        })}
        {...props}
      >
        {children}
      </ul>
    </li>
  );
};

/**
 * @private Do not use directly except in tests and stories. Use {@link NavigationItem} instead
 */
export const DrawerItem: FC<INavigationItemProps> = ({ children }) => {
  const css = useCss();

  return (
    <li
      className={css('item', {
        color: COLOR_PALETTE.grey.darker,
        padding: '2em 3em 2em 2em',
      })}
    >
      {children}
    </li>
  );
};

/**
 * @private Do not use directly except in tests and stories. Use {@link NavigationLink} instead
 */
export const DrawerLink: FC<INavigationLinkProps> = ({ children, icon: Icon, ...props }) => {
  const css = useCss();
  const [isHovered, hoveredRef] = useHoverState();

  return (
    <li
      ref={hoveredRef}
      className={css('item', {
        color: COLOR_PALETTE.grey.darker,
        backgroundColor: isHovered ? COLOR_PALETTE.grey.lighter : 'unset',
      })}
    >
      <Link
        {...props}
        className={css('link', {
          display: 'flex',
          alignItems: 'center',
          color: COLOR_PALETTE.grey.darker,
          cursor: 'pointer',
          boxSizing: 'border-box',
          position: 'relative',
          fontWeight: 'normal',
          textDecoration: 'none',
          flexShrink: 0,
          padding: '2em 3em 2em 2em',
        })}
      >
        <>
          {Icon && (
            <Icon
              className={css('icon', {
                width: '15px',
                height: '15px',
                marginRight: '0.6em',
              })}
            />
          )}
          {children}
        </>
      </Link>
    </li>
  );
};
